import React, { useEffect } from 'react';

import delv from 'dlv';
import styled from 'styled-components';

import { MenuItemCustomization } from 'components/menu-order-wizard/menu-item-customization';
import SelectionContainer from 'components/selection-container';
import { Upsell } from 'pages/cart/upsell';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { isCombo, pickerAspectOptHasResolution } from 'utils/menu';

import useModalCta from './hooks/use-modal-cta';
import PickerDetail from './picker-detail';
import { useWizard } from './wizard-provider';
import { getComboSlotId, getIsComboInSeries, getShouldShowModifiers } from './wizard-ui-helpers';

const StyledUpsell = styled.div`
  margin-bottom: 2rem;
`;

function SeriesWithLastAsModifierUI({
  addToOrderText,
  pickerAspects,
  calorieMap = {},
  handlePickerSelection,
  comboSlotSelections,
  excludesForPickerAspects,
  handleComboSelection,
  handleComboSlotSelectionQuantityUpdate,
  pickerSelection,
  pickerOptions,
  selectedPickerIdentifiers,
  addToOrder,
  handleModifierSelection,
  handleModifierQuantityUpdate,
  modifierSelections,
  pricingFunction,
  quickConfigs,
  currentPrice,
  setModalCta,
  pricePickerAspect,
  pickerAspectPromotionMap,
}) {
  const useEnableMenuUpsellItems = useFlag(LaunchDarklyFlag.ENABLE_MENU_UPSELL_ITEMS);
  const { showStaticMenu } = useMenuContext();

  const {
    actions: { incrementStep },
    state: { currentStep, isFinalStep },
  } = useWizard();

  useModalCta({
    addToOrder,
    addToOrderText,
    currentPrice,
    incrementStep,
    isFinalStep,
    setModalCta,
  });

  const pickerAspectStep =
    currentStep >= pickerAspects.length - 1 ? pickerAspects.length - 1 : currentStep;
  const currentAspect = pickerAspects[pickerAspectStep];
  const initalStepUI = currentStep === 0;

  const filterAspectOptionsForAvailabilty = () => {
    if (pickerAspects.length <= 0) {
      return;
    }

    const previousSelections = pickerAspects
      .slice(0, currentStep)
      .map(aspect => selectedPickerIdentifiers[aspect._id]);

    return currentAspect.pickerAspectOptions.filter(option =>
      pickerAspectOptHasResolution(option.identifier, previousSelections, pickerOptions)
    );
  };

  const filteredCurrentAspect = pickerAspects.length > 0 && {
    ...currentAspect,
    pickerAspectOptions: filterAspectOptionsForAvailabilty(),
  };

  const handlePickerSelectionWithDefaults = (pickerAspectKey, aspectIdentifier) => {
    // ensure that the defaulted values work with this new resolution
    const defaultedPickerAspects = pickerAspects.slice(currentStep + 1, pickerAspects.length);
    const defaultedSelections = defaultedPickerAspects.map(
      aspect => selectedPickerIdentifiers[aspect._id]
    );
    // does the new selection resolve with the new choice
    const canResolve = pickerAspectOptHasResolution(
      aspectIdentifier,
      defaultedSelections,
      pickerOptions
    );
    // if the new option + defaults do not resolve, find an option that does.
    if (!canResolve) {
      // find an option that can resolve
      const possibleResolution = pickerOptions.find(option => {
        const mappingValues = option.pickerItemMappings.map(
          mapping => mapping.pickerAspectValueIdentifier
        );
        return mappingValues.includes(aspectIdentifier);
      });
      // set the other picker mappings to the othee values,
      const possibleResolutionSelections = possibleResolution.pickerItemMappings.map(
        mapping => mapping.pickerAspectValueIdentifier
      );

      defaultedPickerAspects.forEach(pickerAspect => {
        const option = pickerAspect.pickerAspectOptions.find(({ identifier }) =>
          possibleResolutionSelections.includes(identifier)
        );
        handlePickerSelection(pickerAspect._id, option.identifier);
      });
    }

    handlePickerSelection(pickerAspectKey, aspectIdentifier);
  };

  useEffect(
    () => {
      filterAspectOptionsForAvailabilty();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep]
  );

  let calculatedComboSlots = [];
  const comboSlots = isCombo(pickerSelection)
    ? pickerSelection.options.filter(
        slot =>
          slot.uiPattern !== 'hidden' &&
          slot.options.some(opt => pricingFunction(opt.option) !== undefined)
      )
    : [];
  const isComboInSeries = getIsComboInSeries(pickerSelection);
  let showModifiers = true;
  // if the pickerSelection is a combo in series only show one comboSlot at a time when we need to.
  if (comboSlots.length && isComboInSeries) {
    const hasMainItemAndMods = !!pickerSelection.mainItem?.options.length;
    const currentStepAfterPickerAspects = currentStep - pickerAspects.length - 1;
    const comboSlotId = getComboSlotId(
      comboSlots,
      currentStepAfterPickerAspects,
      hasMainItemAndMods
    );
    if (comboSlotId) {
      calculatedComboSlots = [comboSlotId];
    }
    showModifiers = getShouldShowModifiers(currentStepAfterPickerAspects);
  } else {
    calculatedComboSlots = comboSlots.map(({ _id }) => _id);
  }

  let selectedPickerAspectOption;
  if (filteredCurrentAspect) {
    selectedPickerAspectOption = (filteredCurrentAspect.pickerAspectOptions || []).find(
      option => option.identifier === selectedPickerIdentifiers[filteredCurrentAspect._id]
    );
  }

  return (
    <>
      {filteredCurrentAspect && (
        <>
          <SelectionContainer
            key={filteredCurrentAspect.name?.locale}
            title={filteredCurrentAspect.name?.locale}
            uiPattern={filteredCurrentAspect.uiPattern}
            required={!showStaticMenu}
            focusOnMount
            selection={delv(selectedPickerAspectOption, 'name.locale', '')}
          >
            <PickerDetail
              initialStepUI={initalStepUI}
              aspectPromotionMap={pickerAspectPromotionMap}
              aspect={filteredCurrentAspect}
              selectedPickerIdentifiers={selectedPickerIdentifiers}
              handleSelection={handlePickerSelectionWithDefaults}
              calorieMap={calorieMap}
              pricePickerAspect={pricePickerAspect}
              isInStaticMenu={showStaticMenu}
            />
          </SelectionContainer>
        </>
      )}

      {
        // TODO: if pickerSelection is a combo render `series-ui` or `parallel-ui` otherwise show item customization
        currentStep >= pickerAspects.length - 1 && pickerSelection && (
          <>
            <MenuItemCustomization
              menuItem={pickerSelection}
              comboSlotSelections={comboSlotSelections}
              excludesForPickerAspects={excludesForPickerAspects}
              handleComboSelection={handleComboSelection}
              handleComboSlotSelectionQuantityUpdate={handleComboSlotSelectionQuantityUpdate}
              handleModifierSelection={handleModifierSelection}
              handleModifierQuantityUpdate={handleModifierQuantityUpdate}
              modifierSelections={modifierSelections}
              pricingFunction={pricingFunction}
              quickConfigs={quickConfigs}
              showModifiers={showModifiers}
              comboSlotsToShow={calculatedComboSlots}
            />
            {useEnableMenuUpsellItems && (
              <StyledUpsell>
                <Upsell />
              </StyledUpsell>
            )}
          </>
        )
      }
    </>
  );
}

export default SeriesWithLastAsModifierUI;
